import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { io } from 'socket.io-client';
import { stringToNumericUid } from './AgoraContext';

const BreakoutRoomContext = createContext(null);

export function BreakoutRoomProvider({ children, meetingId, localParticipantId, localParticipantName, meetingRole, hostUserId, localMicOn, localWebcamOn }) {
  // Keep refs for mic/cam state and role so we can save the latest values right before redirect
  const localMicOnRef = useRef(localMicOn);
  const localWebcamOnRef = useRef(localWebcamOn);
  const meetingRoleRef = useRef(meetingRole);
  useEffect(() => {
    localMicOnRef.current = localMicOn;
    localWebcamOnRef.current = localWebcamOn;
  }, [localMicOn, localWebcamOn]);
  useEffect(() => {
    meetingRoleRef.current = meetingRole;
  }, [meetingRole]);

  // State
  const [session, setSession] = useState(null);
  const [helpRequests, setHelpRequests] = useState([]);
  const [timerRemaining, setTimerRemaining] = useState(null);
  const [roomTimers, setRoomTimers] = useState({}); // { [roomId]: remainingSeconds }
  const [broadcastMessages, setBroadcastMessages] = useState([]);
  // Restore currentBreakoutRoom from localStorage if we're inside a breakout room after redirect
  const [currentBreakoutRoom, setCurrentBreakoutRoom] = useState(() => {
    const origin = localStorage.getItem('rlink_breakout_origin');
    const roomId = localStorage.getItem('rlink_breakout_roomId');
    const roomName = localStorage.getItem('rlink_breakout_roomName');
    if (origin && roomId && roomName) {
      return { roomId, roomName, agoraChannel: null };
    }
    return null;
  });
  const [breakoutModeratorId, setBreakoutModeratorId] = useState(() => localStorage.getItem('rlink_breakout_moderatorId'));
  const [breakoutModeratorName, setBreakoutModeratorName] = useState(() => localStorage.getItem('rlink_breakout_moderatorName'));
  const [isBreakoutModerator, setIsBreakoutModerator] = useState(() => localStorage.getItem('rlink_breakout_isModerator') === 'true');
  const [isConnected, setIsConnected] = useState(false);
  const [showBreakoutModal, setShowBreakoutModal] = useState(false);

  const socketRef = useRef(null);
  const timerIntervalRef = useRef(null); // for client-side interpolation
  // Tracks participants seen in breakout rooms so SetupTab can show them as "Unassigned"
  // after Clear All. Lives here (not in SetupTab) because SetupTab unmounts on tab switches.
  const knownBreakoutParticipantsRef = useRef(new Map());

  const isHost = meetingRole === 'moderator' || meetingRole === 'co-host' || meetingRole === 'host';

  // Connect socket
  useEffect(() => {
    if (!meetingId) return;

    const baseUrl = process.env.REACT_APP_BASE_URL?.replace('/api', '') || '';
    const socket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
    });

    socketRef.current = socket;

    socket.on('connect', () => {
      setIsConnected(true);
      // Register as host if applicable
      if (isHost) {
        socket.emit('breakout:register-host', { meetingId, hostId: localParticipantId, hostUserId });
      }
      // Join meeting room for broadcast events
      socket.emit('join-meeting', meetingId, localParticipantId, localParticipantName);

      // If participant is inside a breakout room, re-sync room host info
      // (handles case where host was assigned during page redirect)
      const bkRoomId = localStorage.getItem('rlink_breakout_roomId');
      const bkMeetingId = localStorage.getItem('rlink_breakout_meetingId');
      if (bkRoomId && bkMeetingId) {
        console.log('[BreakoutRoom] Re-syncing room host: meetingId=', bkMeetingId, 'roomId=', bkRoomId);
        socket.emit('breakout:get-room-host', { meetingId: bkMeetingId, roomId: bkRoomId });
        // Retry after a delay in case the host assigned the room host right after
        // opening rooms (backend may not have processed it yet on first request)
        setTimeout(() => {
          if (socket.connected) {
            socket.emit('breakout:get-room-host', { meetingId: bkMeetingId, roomId: bkRoomId });
          }
        }, 3000);
        setTimeout(() => {
          if (socket.connected) {
            socket.emit('breakout:get-room-host', { meetingId: bkMeetingId, roomId: bkRoomId });
          }
        }, 8000);
      }

      // If participant just returned from a breakout room to the main room,
      // notify backend to unassign them from the breakout room.
      // Only fire if NOT currently in a breakout room (rlink_breakout_origin absent).
      const returnedFromBreakout = localStorage.getItem('rlink_returned_from_breakout');
      const stillInBreakout = localStorage.getItem('rlink_breakout_origin');
      if (returnedFromBreakout && !stillInBreakout) {
        try {
          const { parentMeetingId: pmId, odId } = JSON.parse(returnedFromBreakout);
          if (pmId && odId) {
            console.log('[BreakoutRoom] Participant returned to main room, unassigning from breakout:', pmId, odId);
            // Backend handles both string/number variants of odId, single emit is sufficient
            socket.emit('breakout:unassign-participant', { meetingId: pmId, odId, immediate: false });
          }
        } catch (_) {}
        localStorage.removeItem('rlink_returned_from_breakout');
      }
    });

    socket.on('disconnect', () => setIsConnected(false));

    // On reconnect, explicitly re-fetch session to recover from any missed events
    socket.io.on('reconnect', () => {
      console.log('[BreakoutRoom] Socket reconnected, re-fetching session');
      socket.emit('breakout:get-session', { meetingId });
    });

    // Error handling
    socket.on('breakout:error', (data) => {
      console.error('[BreakoutRoom] Server error:', data?.message);
      // Reset session if it's stale
      if (data?.message?.includes('expired')) {
        setSession(null);
      }
    });

    // Direct close/return events — when participant is in a breakout room,
    // the backend emits these to the breakout room's socket room directly.
    // Shared handler for both events to prevent logic divergence.
    const handlePrimaryReturnToMain = (eventName) => {
      const originRoom = localStorage.getItem('rlink_breakout_origin');
      if (originRoom && !isHost) {
        console.log(`[BreakoutRoom] Primary socket: ${eventName}, returning to:`, originRoom);
        const parentMeetingId = localStorage.getItem('rlink_breakout_meetingId');
        const breakoutOdId = localStorage.getItem('rlink_breakout_odId');
        // Save for unassign on main room reconnect
        if (parentMeetingId && breakoutOdId) {
          localStorage.setItem('rlink_returned_from_breakout', JSON.stringify({ parentMeetingId, odId: breakoutOdId }));
        }
        localStorage.removeItem('rlink_breakout_origin');
        localStorage.removeItem('rlink_breakout_meetingId');
        localStorage.removeItem('rlink_breakout_odId');
        localStorage.removeItem('rlink_breakout_roomId');
        localStorage.removeItem('rlink_breakout_roomName');
        localStorage.removeItem('rlink_breakout_moderatorId');
        localStorage.removeItem('rlink_breakout_moderatorName');
        localStorage.removeItem('rlink_breakout_isModerator');
        const existingName = localStorage.getItem('rlink_breakout_name');
        const nameToSave = (localParticipantName && localParticipantName !== 'User')
          ? localParticipantName : (existingName || '');
        localStorage.setItem('rlink_breakout_name', nameToSave);
        // Save current mic/cam state before redirect
        localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
        localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');
        localStorage.setItem('rlink_breakout_role', meetingRoleRef.current || '');
        window.__breakoutRedirecting = true;
        window.location.href = `/${originRoom}?fromBreakout=1`;
      }
    };
    socket.on('breakout:rooms-closed', () => handlePrimaryReturnToMain('rooms-closed'));
    socket.on('breakout:return-to-main', () => handlePrimaryReturnToMain('return-to-main'));

    // Host events
    socket.on('breakout:session-updated', (data) => setSession(data));
    // Backend emits the full requests array as `{ requests: [...] }` for both
    // initial sync (on register-host) and after request/respond/cancel.
    socket.on('breakout:help-requested', (data) => {
      setHelpRequests(Array.isArray(data?.requests) ? data.requests : []);
    });
    socket.on('breakout:help-cancelled', (data) => {
      if (Array.isArray(data?.requests)) {
        setHelpRequests(data.requests);
      } else {
        setHelpRequests(prev => prev.filter(h => !(h.odId === data.odId && h.roomId === data.roomId)));
      }
    });
    socket.on('breakout:help-resolved', (data) => {
      setHelpRequests(prev => prev.filter(h => !(h.odId === data.odId && h.roomId === data.roomId)));
    });

    // Participant events — move to breakout room when assigned
    // Host and moderator should never be auto-moved to breakout rooms
    const isProtectedRole = meetingRole === 'host' || meetingRole === 'moderator';
    socket.on('breakout:rooms-opened', (data) => {
      if (isProtectedRole) return; // Host/moderator stays in the main room
      const sess = data.session;
      if (sess && sess.rooms) {
        for (const room of sess.rooms) {
          const numLocalId = stringToNumericUid(localParticipantId);
          const found = (room.participants || []).find(p =>
            String(p.odId) === String(localParticipantId) || String(p.odId) === String(numLocalId)
          );
          if (found) {
            setCurrentBreakoutRoom({
              roomId: room.roomId,
              roomName: room.roomName,
              agoraChannel: `${meetingId}-breakout-${room.roomId}`,
            });
            // Redirect participant to the breakout room URL
            if (room.sourceRoomId) {
              const currentPath = window.location.pathname.replace(/^\//, '');
              localStorage.setItem('rlink_breakout_origin', currentPath);
              localStorage.setItem('rlink_breakout_meetingId', meetingId);
              // Store BOTH the raw participantId and the numeric Agora UID
              // The assignment uses the Agora UID, so unassign must use it too
              const numericOdId = stringToNumericUid(localParticipantId);
              localStorage.setItem('rlink_breakout_odId', String(numericOdId));
              const _bkName104 = (localParticipantName && localParticipantName !== 'User')
                ? localParticipantName
                : (localStorage.getItem('rlink_breakout_name') || '');
              localStorage.setItem('rlink_breakout_name', _bkName104);
              localStorage.setItem('rlink_breakout_roomId', room.roomId);
              localStorage.setItem('rlink_breakout_roomName', room.roomName);
              if (room.moderatorId) {
                localStorage.setItem('rlink_breakout_moderatorId', String(room.moderatorId));
                const mod = (room.participants || []).find(p => String(p.odId) === String(room.moderatorId));
                if (mod) localStorage.setItem('rlink_breakout_moderatorName', mod.odName);
                // Check if current user is the room host
                const isSelfModerator = String(room.moderatorId) === String(localParticipantId) ||
                  String(room.moderatorId) === String(stringToNumericUid(localParticipantId));
                if (isSelfModerator) localStorage.setItem('rlink_breakout_isModerator', 'true');
              }
              // Save current mic/cam state right before redirect so breakout room preserves it
              localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
              localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');
              // Preserve role (e.g. co-host) across the redirect — Conference.js reads this
              // so co-hosts retain their role inside the breakout room.
              localStorage.setItem('rlink_breakout_role', meetingRoleRef.current || '');
              console.log('[BreakoutRoom] rooms-opened: Redirecting to:', `/${room.sourceRoomId}?fromBreakout=1`, 'mic:', localMicOnRef.current, 'cam:', localWebcamOnRef.current);
              window.__breakoutRedirecting = true;
              window.location.href = `/${room.sourceRoomId}?fromBreakout=1`;
            } else {
              console.warn('[BreakoutRoom] rooms-opened: sourceRoomId missing for room', room.roomId);
            }
            break;
          }
        }
      }
    });

    // Individual participant moved to a breakout room (immediate assignment)
    // Host is never auto-moved — they join rooms manually
    socket.on('breakout:participant-moved', (data) => {
      if (isProtectedRole) return; // Host/moderator should never be moved to breakout rooms
      // Agora uses numeric UIDs internally, but localParticipantId is a string.
      // The host sees remote participants by their numeric UID, so data.odId may be numeric.
      // We need to compare both the string ID and its numeric hash equivalent.
      const numericLocalId = stringToNumericUid(localParticipantId);
      const isMe = String(data.odId) === String(localParticipantId) ||
                   String(data.odId) === String(numericLocalId);
      console.log('[BreakoutRoom] participant-moved event received:', JSON.stringify(data), 'localParticipantId:', localParticipantId, 'numericLocalId:', numericLocalId, 'isMe:', isMe, 'isHost:', isHost);
      if (isMe) {
        console.log('[BreakoutRoom] Moving to breakout room:', data.roomName, 'sourceRoomId:', data.sourceRoomId);
        setCurrentBreakoutRoom({
          roomId: data.roomId,
          roomName: data.roomName,
          agoraChannel: data.agoraChannel,
        });
        // Redirect participant to the actual breakout room URL
        if (data.sourceRoomId) {
          // Save state so participant preserves their settings and can be redirected back
          const currentPath = window.location.pathname.replace(/^\//, '');
          localStorage.setItem('rlink_breakout_origin', currentPath);
          localStorage.setItem('rlink_breakout_meetingId', meetingId);
          // Store numeric Agora UID (matches the ID used for assignment)
          const numericOdId2 = stringToNumericUid(localParticipantId);
          localStorage.setItem('rlink_breakout_odId', String(numericOdId2));
          const _bkName151 = (localParticipantName && localParticipantName !== 'User')
            ? localParticipantName
            : (localStorage.getItem('rlink_breakout_name') || '');
          localStorage.setItem('rlink_breakout_name', _bkName151);
          localStorage.setItem('rlink_breakout_roomId', data.roomId);
          localStorage.setItem('rlink_breakout_roomName', data.roomName);
          if (data.moderatorId) {
            localStorage.setItem('rlink_breakout_moderatorId', String(data.moderatorId));
            if (data.moderatorName) localStorage.setItem('rlink_breakout_moderatorName', data.moderatorName);
            // Check if current user is the room host
            const isSelfModerator = String(data.moderatorId) === String(localParticipantId) ||
              String(data.moderatorId) === String(numericLocalId);
            if (isSelfModerator) localStorage.setItem('rlink_breakout_isModerator', 'true');
          }
          // Save current mic/cam state right before redirect so breakout room preserves it
          localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
          localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');
          // Preserve role (e.g. co-host) across the redirect so they keep host privileges
          // inside the breakout room (Conference.js reads this on entry).
          localStorage.setItem('rlink_breakout_role', meetingRoleRef.current || '');
          console.log('[BreakoutRoom] Redirecting to:', `/${data.sourceRoomId}?fromBreakout=1`, 'mic:', localMicOnRef.current, 'cam:', localWebcamOnRef.current);
          window.__breakoutRedirecting = true;
          window.location.href = `/${data.sourceRoomId}?fromBreakout=1`;
        } else {
          console.warn('[BreakoutRoom] sourceRoomId is missing! Cannot redirect participant. Full data:', data);
        }
      }
    });

    // Individual participant returned to main room (immediate unassign)
    socket.on('breakout:participant-returned', (data) => {
      const numId = stringToNumericUid(localParticipantId);
      if (String(data.odId) === String(localParticipantId) || String(data.odId) === String(numId)) {
        console.log('[BreakoutRoom] Returning to main room');
        setCurrentBreakoutRoom(null);
      }
    });

    socket.on('breakout:return-to-main', () => {
      setCurrentBreakoutRoom(null);
      setTimerRemaining(null);
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    });

    socket.on('breakout:rooms-closed', () => {
      setCurrentBreakoutRoom(null);
      setTimerRemaining(null);
      setRoomTimers({});
      setHelpRequests([]);
      setBroadcastMessages([]);
      // Clear participants from session rooms so ManageTab/SetupTab show empty rooms
      setSession(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          status: 'closed',
          rooms: (prev.rooms || []).map(r => ({ ...r, participants: [], moderatorId: null })),
        };
      });
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    });

    socket.on('breakout:broadcast', (data) => {
      setBroadcastMessages(prev => {
        // Deduplicate: skip if we already have a message with the same sentAt + message
        const isDupe = prev.some(m => m.message === data.message && String(m.sentAt) === String(data.sentAt));
        if (isDupe) return prev;
        return [...prev, data];
      });
      // Show prominent toast so participant notices even if sidebar is closed
      if (!isHost) {
        import('react-toastify').then(({ toast }) => {
          toast.info(`📢 ${data.from}: ${data.message}`, {
            position: 'top-center',
            autoClose: 12000,
            theme: 'dark',
            style: { background: '#1a1a3e', border: '1px solid rgba(106, 31, 191, 0.5)' },
          });
        }).catch(() => {});
      }
      // Auto-remove after 15 seconds
      setTimeout(() => {
        setBroadcastMessages(prev => prev.filter(m => m !== data));
      }, 15000);
    });

    // Room host (moderator) changed while participant is inside a breakout room
    socket.on('breakout:room-host-changed', ({ roomId, moderatorId: newModId, moderatorName: newModName }) => {
      const myRoomId = localStorage.getItem('rlink_breakout_roomId');
      console.log('[BreakoutRoom] room-host-changed received: roomId=', roomId, 'myRoomId=', myRoomId, 'modId=', newModId, 'modName=', newModName, 'localParticipantId=', localParticipantId, 'myOdId=', localStorage.getItem('rlink_breakout_odId'));
      if (myRoomId && myRoomId === roomId) {
        console.log('[BreakoutRoom] Room host changed (MATCHED):', newModId, newModName);
        if (newModId) {
          localStorage.setItem('rlink_breakout_moderatorId', String(newModId));
          localStorage.setItem('rlink_breakout_moderatorName', newModName || '');
        } else {
          localStorage.removeItem('rlink_breakout_moderatorId');
          localStorage.removeItem('rlink_breakout_moderatorName');
          localStorage.removeItem('rlink_breakout_isModerator');
        }
        // Compare against both the current participantId AND the original main-channel odId
        const myOdId = localStorage.getItem('rlink_breakout_odId');
        const isSelfMod = newModId && (
          String(newModId) === String(localParticipantId) ||
          (myOdId && String(newModId) === String(myOdId))
        );
        if (isSelfMod) {
          localStorage.setItem('rlink_breakout_isModerator', 'true');
        } else {
          localStorage.removeItem('rlink_breakout_isModerator');
        }
        setBreakoutModeratorId(newModId ? String(newModId) : null);
        setBreakoutModeratorName(newModName || null);
        setIsBreakoutModerator(!!isSelfMod);
      }
    });

    socket.on('breakout:timer-update', ({ remainingSeconds }) => {
      setTimerRemaining(remainingSeconds);
      // Start client-side interpolation
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      let localRemaining = remainingSeconds;
      timerIntervalRef.current = setInterval(() => {
        localRemaining -= 1;
        if (localRemaining <= 0) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
          setTimerRemaining(0);
        } else {
          setTimerRemaining(localRemaining);
        }
      }, 1000);
    });

    socket.on('breakout:room-timer-update', ({ roomId, remainingSeconds }) => {
      setRoomTimers(prev => ({ ...prev, [roomId]: remainingSeconds }));
    });

    socket.on('breakout:timer-warning', (data) => {
      const msg = data.message || `Breakout session ending soon`;
      setBroadcastMessages(prev => [...prev, {
        message: msg,
        from: 'System',
        sentAt: new Date().toISOString(),
        isWarning: true,
      }]);
      // Always show a toast so the warning is visible even if the sidebar is collapsed
      import('react-toastify').then(({ toast }) => {
        toast.warning(`⏰ ${msg}`, {
          position: 'top-center',
          autoClose: 12000,
          theme: 'dark',
          style: { background: '#1a1a2e', border: '1px solid rgba(255,165,0,0.5)' },
          toastId: 'breakout-timer-warning',
        });
      }).catch(() => {});
    });

    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      if (socket) socket.disconnect();
    };
  }, [meetingId, localParticipantId, localParticipantName, isHost]);

  // Secondary socket: if participant is in a breakout room, connect back to
  // the parent meeting's socket to listen for close/return events
  const parentSocketRef = useRef(null);
  useEffect(() => {
    const originRoom = localStorage.getItem('rlink_breakout_origin');
    const parentMeetingId = localStorage.getItem('rlink_breakout_meetingId');
    const breakoutOdId = localStorage.getItem('rlink_breakout_odId');
    if (!originRoom || !parentMeetingId || isHost) return;

    const baseUrl = process.env.REACT_APP_BASE_URL?.replace('/api', '') || '';
    const parentSocket = io(baseUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: 5,
    });
    parentSocketRef.current = parentSocket;

    // Track retry timeouts so cleanup can cancel them — otherwise they fire
    // after unmount and emit on a disconnected socket.
    const pendingTimeouts = [];
    parentSocket.on('connect', () => {
      console.log('[BreakoutRoom] Connected to parent meeting socket for return events');
      parentSocket.emit('join-meeting', parentMeetingId, breakoutOdId, localParticipantName);
      // Re-sync room host info (handles case where host was assigned during page redirect)
      const bkRoomId = localStorage.getItem('rlink_breakout_roomId');
      if (bkRoomId) {
        parentSocket.emit('breakout:get-room-host', { meetingId: parentMeetingId, roomId: bkRoomId });
        // Retry after delays in case host assigns room host shortly after opening rooms
        pendingTimeouts.push(setTimeout(() => {
          if (parentSocket.connected) {
            parentSocket.emit('breakout:get-room-host', { meetingId: parentMeetingId, roomId: bkRoomId });
          }
        }, 3000));
        pendingTimeouts.push(setTimeout(() => {
          if (parentSocket.connected) {
            parentSocket.emit('breakout:get-room-host', { meetingId: parentMeetingId, roomId: bkRoomId });
          }
        }, 8000));
      }
    });

    // Listen for events that should send participant back to main room
    const returnToMain = () => {
      console.log('[BreakoutRoom] Returning to main room:', originRoom);
      // Clear breakout state
      localStorage.removeItem('rlink_breakout_origin');
      localStorage.removeItem('rlink_breakout_meetingId');
      localStorage.removeItem('rlink_breakout_odId');
      localStorage.removeItem('rlink_breakout_roomId');
      localStorage.removeItem('rlink_breakout_roomName');
      localStorage.removeItem('rlink_breakout_moderatorId');
      localStorage.removeItem('rlink_breakout_moderatorName');
      localStorage.removeItem('rlink_breakout_isModerator');
      // Re-set the participant name so Conference.js can read it on the main room side
      // (the breakout room's Conference.js already consumed and removed it)
      const existingBkName = localStorage.getItem('rlink_breakout_name');
      const nameToSave = (localParticipantName && localParticipantName !== 'User')
        ? localParticipantName
        : (existingBkName || '');
      localStorage.setItem('rlink_breakout_name', nameToSave);
      // Save current mic/cam state before redirect
      localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
      localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');
      // Add ?fromBreakout=1 so Conference.js auto-starts (skips lobby)
      window.__breakoutRedirecting = true;
      window.location.href = `/${originRoom}?fromBreakout=1`;
    };

    // Timer events — show countdown and warning to participant in breakout room
    parentSocket.on('breakout:timer-update', ({ remainingSeconds }) => {
      setTimerRemaining(remainingSeconds);
      // Start client-side interpolation so timer counts down smoothly between server ticks
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
      let localRemaining = remainingSeconds;
      timerIntervalRef.current = setInterval(() => {
        localRemaining -= 1;
        if (localRemaining <= 0) {
          clearInterval(timerIntervalRef.current);
          timerIntervalRef.current = null;
          setTimerRemaining(0);
        } else {
          setTimerRemaining(localRemaining);
        }
      }, 1000);
    });

    parentSocket.on('breakout:room-timer-update', ({ roomId, remainingSeconds }) => {
      // Update per-room timer — participant uses this when individual timers are on
      const myRoomId = localStorage.getItem('rlink_breakout_roomId');
      if (roomId === myRoomId) {
        setTimerRemaining(remainingSeconds);
        // Client-side interpolation
        if (timerIntervalRef.current) clearInterval(timerIntervalRef.current);
        let localRemaining = remainingSeconds;
        timerIntervalRef.current = setInterval(() => {
          localRemaining -= 1;
          if (localRemaining <= 0) {
            clearInterval(timerIntervalRef.current);
            timerIntervalRef.current = null;
            setTimerRemaining(0);
          } else {
            setTimerRemaining(localRemaining);
          }
        }, 1000);
      }
      setRoomTimers(prev => ({ ...prev, [roomId]: remainingSeconds }));
    });

    parentSocket.on('breakout:timer-warning', () => {
      // Primary socket on meeting:sourceRoomId now also receives this event (backend fix),
      // so the banner notification is handled there. Nothing extra needed here.
    });

    // Room host changed while participant is in a breakout room (via parent meeting channel)
    parentSocket.on('breakout:room-host-changed', ({ roomId, moderatorId: newModId, moderatorName: newModName }) => {
      const myRoomId = localStorage.getItem('rlink_breakout_roomId');
      if (myRoomId && myRoomId === roomId) {
        console.log('[BreakoutRoom] (parent) Room host changed:', newModId, newModName);
        if (newModId) {
          localStorage.setItem('rlink_breakout_moderatorId', String(newModId));
          localStorage.setItem('rlink_breakout_moderatorName', newModName || '');
        } else {
          localStorage.removeItem('rlink_breakout_moderatorId');
          localStorage.removeItem('rlink_breakout_moderatorName');
          localStorage.removeItem('rlink_breakout_isModerator');
        }
        const isSelfMod = newModId && String(newModId) === String(breakoutOdId);
        if (isSelfMod) {
          localStorage.setItem('rlink_breakout_isModerator', 'true');
        } else {
          localStorage.removeItem('rlink_breakout_isModerator');
        }
        setBreakoutModeratorId(newModId ? String(newModId) : null);
        setBreakoutModeratorName(newModName || null);
        setIsBreakoutModerator(!!isSelfMod);
      }
    });

    parentSocket.on('breakout:rooms-closed', returnToMain);
    parentSocket.on('breakout:return-to-main', returnToMain);
    // Reassignment — participant moved to a different breakout room while already in one
    parentSocket.on('breakout:participant-moved', (data) => {
      const numBreakoutId = stringToNumericUid(breakoutOdId);
      const isMe = String(data.odId) === String(breakoutOdId) ||
                   String(data.odId) === String(numBreakoutId);
      if (isMe && data.sourceRoomId) {
        console.log('[BreakoutRoom] Reassigned to different breakout room:', data.roomName);
        // Update stored breakout room info before redirect
        localStorage.setItem('rlink_breakout_roomId', data.roomId);
        localStorage.setItem('rlink_breakout_roomName', data.roomName);
        if (data.moderatorId) {
          localStorage.setItem('rlink_breakout_moderatorId', String(data.moderatorId));
          if (data.moderatorName) localStorage.setItem('rlink_breakout_moderatorName', data.moderatorName);
        }
        localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
        localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');
        // Preserve role (e.g. co-host) across the redirect when reassigned to a different breakout.
        localStorage.setItem('rlink_breakout_role', meetingRoleRef.current || '');
        window.__breakoutRedirecting = true;
        window.location.href = `/${data.sourceRoomId}?fromBreakout=1`;
      }
    });
    // Single room close — only redirect if this participant is in the closed room
    parentSocket.on('breakout:single-room-closed', (data) => {
      const myRoomId = localStorage.getItem('rlink_breakout_roomId');
      if (data.roomId && myRoomId && data.roomId === myRoomId) {
        returnToMain();
      }
    });
    // Broadcast messages are handled by the primary socket (backend emits
    // directly to each breakout room). No listener here to avoid duplicates.
    parentSocket.on('breakout:participant-returned', (data) => {
      const numBreakoutId = stringToNumericUid(breakoutOdId);
      if (String(data.odId) === String(breakoutOdId) || String(data.odId) === String(numBreakoutId)) {
        returnToMain();
      }
    });

    return () => {
      // Cancel pending retry timeouts before tearing down the socket
      pendingTimeouts.forEach(clearTimeout);
      // Stop the client-side timer interpolation if a breakout timer was running
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      if (parentSocket) {
        // Detach all listeners we registered (`connect`, `breakout:*`).
        // disconnect() alone does not remove handler closures synchronously,
        // so without this they can still fire on in-flight buffered events.
        parentSocket.removeAllListeners();
        parentSocket.disconnect();
      }
    };
  }, [isHost, localParticipantName]);

  // Host actions
  const createSession = useCallback(() => {
    socketRef.current?.emit('breakout:create-session', { meetingId, hostUserId });
  }, [meetingId, hostUserId]);

  const addRoom = useCallback((roomName, sourceRoomId) => {
    socketRef.current?.emit('breakout:add-room', { meetingId, roomName, sourceRoomId });
  }, [meetingId]);

  const removeRoom = useCallback((roomId) => {
    socketRef.current?.emit('breakout:remove-room', { meetingId, roomId });
  }, [meetingId]);

  const renameRoom = useCallback((roomId, roomName) => {
    const trimmed = typeof roomName === 'string' ? roomName.trim() : '';
    if (!trimmed) return;
    socketRef.current?.emit('breakout:rename-room', { meetingId, roomId, roomName: trimmed });
    // Optimistic update so the UI reflects the change immediately
    setSession(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        rooms: (prev.rooms || []).map(r =>
          r.roomId === roomId ? { ...r, roomName: trimmed } : r
        ),
      };
    });
  }, [meetingId]);

  const assignParticipant = useCallback((odId, odName, role, roomId, immediate = false) => {
    socketRef.current?.emit('breakout:assign-participant', { meetingId, odId, odName, role, roomId, immediate });
  }, [meetingId]);

  const unassignParticipant = useCallback((odId, immediate = false) => {
    socketRef.current?.emit('breakout:unassign-participant', { meetingId, odId, immediate });
  }, [meetingId]);

  const autoAssign = useCallback((method, participants) => {
    socketRef.current?.emit('breakout:auto-assign', { meetingId, method, participants });
  }, [meetingId]);

  const clearAll = useCallback(() => {
    socketRef.current?.emit('breakout:clear-all', { meetingId });
  }, [meetingId]);

  const openRooms = useCallback(() => {
    socketRef.current?.emit('breakout:open-rooms', { meetingId });
  }, [meetingId]);

  const closeRooms = useCallback(() => {
    socketRef.current?.emit('breakout:close-rooms', { meetingId });
  }, [meetingId]);

  // Fetch latest session state from server (defensive re-sync)
  const refreshSession = useCallback(() => {
    if (socketRef.current?.connected && meetingId) {
      socketRef.current.emit('breakout:get-session', { meetingId });
    }
  }, [meetingId]);

  const closeRoom = useCallback((roomId) => {
    socketRef.current?.emit('breakout:close-room', { meetingId, roomId });
  }, [meetingId]);

  const broadcastMessage = useCallback((message, targetRoomIds) => {
    console.log('[BreakoutRoom] broadcastMessage called:', message, 'targets:', targetRoomIds || 'all', 'socket connected:', socketRef.current?.connected, 'meetingId:', meetingId);
    socketRef.current?.emit('breakout:broadcast-message', { meetingId, message, targetRoomIds: targetRoomIds || null });
  }, [meetingId]);

  const updateSettings = useCallback((settings) => {
    socketRef.current?.emit('breakout:update-settings', { meetingId, settings });
    // Optimistic update so controlled inputs reflect the change immediately
    setSession(prev => {
      if (!prev) return prev;
      return { ...prev, settings: { ...prev.settings, ...settings } };
    });
  }, [meetingId]);

  const extendTime = useCallback((additionalMinutes) => {
    socketRef.current?.emit('breakout:extend-time', { meetingId, additionalMinutes });
  }, [meetingId]);

  const setRoomTimer = useCallback((roomId, durationMinutes) => {
    socketRef.current?.emit('breakout:set-room-timer', { meetingId, roomId, durationMinutes });
    // Optimistic update so the select reflects the change immediately
    setSession(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        rooms: (prev.rooms || []).map(r =>
          r.roomId === roomId ? { ...r, timerDuration: durationMinutes * 60 } : r
        ),
      };
    });
  }, [meetingId]);

  const respondToHelp = useCallback((roomId, odId) => {
    socketRef.current?.emit('breakout:respond-help', { meetingId, roomId, odId });
  }, [meetingId]);

  const assignRoomHost = useCallback((roomId, odId) => {
    socketRef.current?.emit('breakout:assign-room-host', { meetingId, roomId, odId });
  }, [meetingId]);

  // Host action: manually join a breakout room (redirect-based, mirrors participant flow)
  const joinRoom = useCallback((roomId, roomName) => {
    const room = (session?.rooms || []).find(r => r.roomId === roomId);
    if (!room?.sourceRoomId) {
      console.warn('[BreakoutRoom] joinRoom: sourceRoomId missing for room', roomId, '- rooms may not be open yet');
      return;
    }

    setCurrentBreakoutRoom({
      roomId,
      roomName,
      agoraChannel: `${meetingId}-breakout-${roomId}`,
    });

    const originPath = window.location.pathname.replace(/^\//, '');
    const numericOdId = stringToNumericUid(localParticipantId);
    localStorage.setItem('rlink_breakout_origin', originPath);
    localStorage.setItem('rlink_breakout_meetingId', meetingId);
    localStorage.setItem('rlink_breakout_odId', String(numericOdId));
    const nameToSave = (localParticipantName && localParticipantName !== 'User')
      ? localParticipantName
      : (localStorage.getItem('rlink_breakout_name') || '');
    localStorage.setItem('rlink_breakout_name', nameToSave);
    localStorage.setItem('rlink_breakout_roomId', roomId);
    localStorage.setItem('rlink_breakout_roomName', roomName);
    localStorage.setItem('rlink_breakout_role', meetingRoleRef.current || '');
    localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
    localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');

    console.log('[BreakoutRoom] Host joinRoom: redirecting to', `/${room.sourceRoomId}?fromBreakout=1`);
    window.__breakoutRedirecting = true;
    window.location.href = `/${room.sourceRoomId}?fromBreakout=1`;
  }, [meetingId, session, localParticipantId, localParticipantName]);

  // Host action: leave breakout room and return to main (redirect back to origin)
  const leaveRoom = useCallback(() => {
    const originRoom = localStorage.getItem('rlink_breakout_origin');
    setCurrentBreakoutRoom(null);

    // If host is currently inside a breakout room URL, redirect back to main
    if (originRoom) {
      localStorage.removeItem('rlink_breakout_origin');
      localStorage.removeItem('rlink_breakout_meetingId');
      localStorage.removeItem('rlink_breakout_odId');
      localStorage.removeItem('rlink_breakout_roomId');
      localStorage.removeItem('rlink_breakout_roomName');
      localStorage.removeItem('rlink_breakout_moderatorId');
      localStorage.removeItem('rlink_breakout_moderatorName');
      localStorage.removeItem('rlink_breakout_isModerator');
      localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
      localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');

      console.log('[BreakoutRoom] Host leaveRoom: redirecting to', `/${originRoom}?fromBreakout=1`);
      window.__breakoutRedirecting = true;
      window.location.href = `/${originRoom}?fromBreakout=1`;
    }
  }, []);

  // Participant actions
  const requestHelp = useCallback(() => {
    if (currentBreakoutRoom) {
      // Inside a breakout room, `meetingId` is the breakout's id, but the host is
      // registered in `hosts:${parentMeetingId}`. Use the parent id from localStorage
      // so the help request reaches the host's socket room.
      const parentMeetingId = localStorage.getItem('rlink_breakout_meetingId') || meetingId;
      socketRef.current?.emit('breakout:request-help', {
        meetingId: parentMeetingId,
        roomId: currentBreakoutRoom.roomId,
        odId: localParticipantId,
        odName: localParticipantName,
      });
    }
  }, [meetingId, currentBreakoutRoom, localParticipantId, localParticipantName]);

  // Leave breakout room and return to main room — emits unassign on the existing socket
  const leaveBreakoutAndReturn = useCallback(() => {
    const originRoom = localStorage.getItem('rlink_breakout_origin');
    if (!originRoom) return false;

    const parentMeetingId = localStorage.getItem('rlink_breakout_meetingId');
    const breakoutOdId = localStorage.getItem('rlink_breakout_odId');
    const existingName = localStorage.getItem('rlink_breakout_name');
    const currentName = existingName || localParticipantName || '';

    console.log('[BreakoutRoom] leaveBreakoutAndReturn: unassigning and redirecting to', originRoom);

    // Emit unassign on the already-connected socket (try both string and numeric odId)
    if (parentMeetingId && breakoutOdId && socketRef.current?.connected) {
      socketRef.current.emit('breakout:unassign-participant', {
        meetingId: parentMeetingId,
        odId: breakoutOdId,
        immediate: false,
      });
      if (!isNaN(breakoutOdId)) {
        socketRef.current.emit('breakout:unassign-participant', {
          meetingId: parentMeetingId,
          odId: Number(breakoutOdId),
          immediate: false,
        });
      }
    }

    // Clear breakout localStorage
    localStorage.removeItem('rlink_breakout_origin');
    localStorage.removeItem('rlink_breakout_meetingId');
    localStorage.removeItem('rlink_breakout_odId');
    localStorage.removeItem('rlink_breakout_roomId');
    localStorage.removeItem('rlink_breakout_roomName');
    localStorage.removeItem('rlink_breakout_moderatorId');
    localStorage.removeItem('rlink_breakout_moderatorName');
    localStorage.removeItem('rlink_breakout_isModerator');
    localStorage.setItem('rlink_breakout_name', currentName);

    // Save current mic/cam state right before redirect so main room preserves it
    localStorage.setItem('rlink_meeting_micOn', localMicOnRef.current ? '1' : '0');
    localStorage.setItem('rlink_meeting_webcamOn', localWebcamOnRef.current ? '1' : '0');

    // Short delay to let the unassign reach the server, then redirect
    setTimeout(() => {
      console.log('[BreakoutRoom] leaveBreakoutAndReturn: redirecting with mic:', localMicOnRef.current, 'cam:', localWebcamOnRef.current);
      window.__breakoutRedirecting = true;
      window.location.href = `/${originRoom}?fromBreakout=1`;
    }, 300);
    return true;
  }, [localParticipantName]);

  const cancelHelp = useCallback(() => {
    if (currentBreakoutRoom) {
      const parentMeetingId = localStorage.getItem('rlink_breakout_meetingId') || meetingId;
      socketRef.current?.emit('breakout:cancel-help', {
        meetingId: parentMeetingId,
        roomId: currentBreakoutRoom.roomId,
        odId: localParticipantId,
      });
    }
  }, [meetingId, currentBreakoutRoom, localParticipantId]);

  // Computed values
  const isActive = session?.status === 'active';
  const isSetup = session?.status === 'setup';
  const rooms = session?.rooms || [];

  // Calculate unassigned from session
  const assignedIds = new Set();
  rooms.forEach(room => {
    (room.participants || []).forEach(p => assignedIds.add(p.odId));
  });

  const value = {
    session,
    rooms,
    isActive,
    isSetup,
    isConnected,
    isHost,
    currentBreakoutRoom,
    breakoutModeratorId,
    breakoutModeratorName,
    isBreakoutModerator,
    helpRequests,
    timerRemaining,
    roomTimers,
    broadcastMessages,
    showBreakoutModal,
    setShowBreakoutModal,
    assignedParticipantIds: assignedIds,
    knownBreakoutParticipantsRef,
    // Host actions
    createSession,
    addRoom,
    removeRoom,
    renameRoom,
    assignParticipant,
    unassignParticipant,
    autoAssign,
    clearAll,
    openRooms,
    closeRooms,
    closeRoom,
    refreshSession,
    broadcastMessage,
    updateSettings,
    extendTime,
    setRoomTimer,
    respondToHelp,
    assignRoomHost,
    joinRoom,
    leaveRoom,
    // Participant actions
    requestHelp,
    cancelHelp,
    leaveBreakoutAndReturn,
  };

  return (
    <BreakoutRoomContext.Provider value={value}>
      {children}
    </BreakoutRoomContext.Provider>
  );
}

export function useBreakoutRooms() {
  const context = useContext(BreakoutRoomContext);
  if (!context) {
    return {
      session: null, rooms: [], isActive: false, isSetup: false, isConnected: false, isHost: false,
      currentBreakoutRoom: null, breakoutModeratorId: null, breakoutModeratorName: null, isBreakoutModerator: false, helpRequests: [], timerRemaining: null, roomTimers: {}, broadcastMessages: [],
      showBreakoutModal: false, setShowBreakoutModal: () => {}, assignedParticipantIds: new Set(),
      createSession: () => {}, addRoom: () => {}, removeRoom: () => {}, assignParticipant: () => {},
      unassignParticipant: () => {}, autoAssign: () => {}, clearAll: () => {}, openRooms: () => {},
      closeRooms: () => {}, broadcastMessage: () => {}, updateSettings: () => {}, extendTime: () => {}, setRoomTimer: () => {},
      respondToHelp: () => {}, assignRoomHost: () => {}, joinRoom: () => {}, leaveRoom: () => {},
      requestHelp: () => {}, cancelHelp: () => {},
    };
  }
  return context;
}

export default BreakoutRoomContext;
